import axios, { AxiosResponse } from 'axios';
import { Adjustment, Adjustments, ImportAdjustmentsStructure, ModifiedAdjustments } from '../../../models/adjustment.types';
import { ImportExaminerStructureRequest, ImportExaminerStructureResponse } from '../../../models/importExaminer.types';
import {
  AnnotationAssignmentStoreDefinition,
  ExamSheetAnnotations,
  ExamSheetStructureWithAnnotations,
} from '../../../pages/AnnotationsAssignment/AnnotationsAssignment.types';
import {
  IControlTaskDistributionPatch,
  IControlTasksDistribution,
} from '../../../pages/Sessions/SessionsParametersDialog/ControlTasksDistribution/ControlTasksDistribution.types';
import {
  ExaminerBlockingLimit,
  ExaminerBlockingLimitPatch,
} from '../../../pages/Sessions/SessionsParametersDialog/ExaminerBlockingLimits/ExaminerBlockingLimits.types';
import { ExaminerIndividualBlockingLimitsFilter, ExaminerIndividualBlockingLimitsResponse, ExaminerIndividualBlockingLimitsSortBy, ExaminerIndividualBlockingLimitsSortById, translateLimitsSortByIdToLimitsSortBy } from '../../../pages/Sessions/SessionsParametersDialog/ExaminerIndividualBlockingLimits/ExaminerIndividualBlockingLimits.types';
import {
  EligibilityThresholds,
  QualificationThresholds,
} from '../../../pages/Sessions/SessionsParametersDialog/QualitifcationThreshold/QualificationThreshold.types';
import EnvWindow from '../../../utils/env';
import { addParam } from '../../../utils/servicesHelper';


export async function importExaminerStructure(
  examSheetId: string,
  deleteExistingStructure: boolean,
  requestBody?: ImportExaminerStructureRequest,
): Promise<AxiosResponse<ImportExaminerStructureResponse>> {
  if (requestBody) requestBody.deleteExistingStructure = deleteExistingStructure;
  return await axios.post(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/examinerStructure`,
    requestBody,
  );
}

export async function importExaminerStructureOKE(
  examSheetId: string,
  deleteExistingStructure: boolean,
  examCommittee: string,
): Promise<AxiosResponse<ImportExaminerStructureResponse>> {
  let params = '';
  params += addParam('deleteExistingStructure', deleteExistingStructure ? deleteExistingStructure : false);
  return await axios.post(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/ExamCommittee/${examCommittee}/examinerStructure?${params}`,
  );
}

export async function getExaminersBlockingLimitsBySheetId(
  examSheetId: string,
): Promise<AxiosResponse<ExaminerBlockingLimit[]>> {
  return await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/BlockingLimits`);
}

export async function getExaminersBlockingLimits(
  examTaskGroupId: string,
  filter: ExaminerIndividualBlockingLimitsFilter,
  sortByColumnId: ExaminerIndividualBlockingLimitsSortById = 'name',
  isDescending = true,
  pageNumber: number,
  pageSize: number,
  signal?: AbortSignal,

): Promise<AxiosResponse<ExaminerIndividualBlockingLimitsResponse>> {
  let params = '';
  params += addParam('name', filter.name ? filter.name : '');
  params += addParam('examinerNumber', filter.examinerNumber ? filter.examinerNumber : '');
  params += addParam('blockingLimitsType', filter.limits ? filter.limits.toString() : '');
  params += addParam('isDescending', isDescending ? 'true' : 'false');
  if (sortByColumnId !== undefined) {
    params += addParam('sortBy', translateLimitsSortByIdToLimitsSortBy(sortByColumnId));
  }

  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup/${examTaskGroupId}/ExaminerBlockingLimits?page=${pageNumber}&pageSize=${pageSize}${params}`, {
    signal: signal ?? undefined,
  });
  return response;
}

export async function updateExaminerIndividualLimits(
  examinerPermissionId: string,
  totalLimitOutOfTolerance: number | null,
  sequentialLimitOutOfTolerance: number | null,
  totalLimitWithinTolerance: number | null,
  sequentialLimitWithinTolerance: number | null,
): Promise<AxiosResponse<boolean>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExaminerPermission/${examinerPermissionId}/ExaminerBlockingLimits`, {
    totalLimitOutOfTolerance,
    sequentialLimitOutOfTolerance,
    totalLimitWithinTolerance,
    sequentialLimitWithinTolerance,
  });
  return response;
}

export async function deleteExaminerIndividualLimits(
  examinerPermissionId: string,
): Promise<AxiosResponse<boolean>> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExaminerPermission/${examinerPermissionId}/ExaminerBlockingLimits`);
  return response;
}

export async function changeExaminerBlockingLimits(
  blockingLimits: ExaminerBlockingLimitPatch[],
  isExaminerView: boolean,
): Promise<AxiosResponse<boolean>> {
  return !isExaminerView
    ? await axios.patch(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/blockingLimits`, { blockingLimits })
    : await axios.patch(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/BlockingLimits`, blockingLimits[0]);
}

export async function getQualificationThresholds(
  examSheetId: string,
  isExaminerView?: boolean,
): Promise<QualificationThresholds[]> {
  if (!isExaminerView) {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/thresholds`);
    return response.data;
  } else {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/thresholds`);
    return [response.data];
  }
}

export async function saveQualificationThresholds(
  eligibilityThresholds: EligibilityThresholds,
  isExaminerView?: boolean,
): Promise<AxiosResponse<boolean>> {
  if (!isExaminerView) {
    return await axios.patch(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/thresholds`, eligibilityThresholds);
  } else {
    return await axios.patch(
      `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/thresholds`,
      eligibilityThresholds.eligibilityThresholds[0],
    );
  }
}

export async function getControlTasksDistribution(
  examSheetId: string,
  isExaminerView?: boolean,
): Promise<IControlTasksDistribution[]> {
  if (!isExaminerView) {
    const response = await axios.get(
      `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/distributionDetails`,
    );
    return response.data;
  } else {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/distributionDetails`);
    return [response.data];
  }
}

export async function saveControlTasksDistribution(
  controlTasksDistribution: IControlTaskDistributionPatch[],
  isExaminerView?: boolean,
): Promise<AxiosResponse<boolean>> {
  if (!isExaminerView) {
    return await axios.patch(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/distributionDetails`, {
      distributionDetails: controlTasksDistribution,
    });
  } else {
    return await axios.patch(
      `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/distributionDetails`,
      controlTasksDistribution[0],
    );
  }
}

export async function getExamSheetStructureWithAnnotations(
  examSheetId: string,
): Promise<AxiosResponse<ExamSheetStructureWithAnnotations>> {
  return await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/AnnotationDefinition`);
}

export async function getExamSheetAnnotations(
  examScoringSchemaId: string,
): Promise<AxiosResponse<ExamSheetAnnotations>> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__EXAM_SCORING_SCHEMA_API_URL}/${examScoringSchemaId}/AnnotationDefinition`,
  );
  return response;
}

export async function storeExamSheetAnnotations(
  scoringSchemaDtos: AnnotationAssignmentStoreDefinition[],
): Promise<AxiosResponse<boolean>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS__EXAM_SCORING_SCHEMA_API_URL}/AnnotationDefinition`, {
    scoringSchemaDtos,
  });
  return response;
}

export async function getAdjustments(examSheetId: string): Promise<Adjustments> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/Adjustments`);
  return response.data;
}

export async function importAdjustments(
  examSheetId: string,
  requestBody?: ImportAdjustmentsStructure,
): Promise<AxiosResponse<boolean>> {
  return axios.put(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/adjustment`, requestBody);
}

export async function importAdjustmentsFromSioeo(examSheetId: string): Promise<AxiosResponse<boolean>> {
  return axios.put(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/adjustmentFromSioeo`);
}

export async function updateAdjustments(examSheetId: string, adjustments: { adjustments: Adjustment[]; adjustmentsPermissions: Adjustment[] }, isKK: boolean): Promise<AxiosResponse<boolean>> {
  const url = isKK ? `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/Adjustments` : `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/Adjustments`;
  return axios.patch(url, adjustments);
}
